import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PathCard from './PathCard';
import ConversationalForm from './ConversationalForm';
import { getAllPaths } from '../../data/pathsConfig';

// ═══════════════════════════════════════════════════════════════════
// TRIAGE PORTAL - Main interaction section with path selection
// ═══════════════════════════════════════════════════════════════════

const TriagePortal = () => {
  const [selectedPath, setSelectedPath] = useState(null);
  const [backgroundGradient, setBackgroundGradient] = useState('');
  const paths = getAllPaths();

  // Update background gradient based on selected path
  useEffect(() => {
    if (selectedPath) {
      const path = paths.find(p => p.id === selectedPath);
      if (path) {
        setBackgroundGradient(`linear-gradient(135deg, ${path.colorHex}10 0%, transparent 50%)`);
      }
    } else {
      setBackgroundGradient('');
    }
  }, [selectedPath, paths]);

  const handlePathSelect = (pathId) => {
    if (selectedPath === pathId) {
      setSelectedPath(null); // Deselect if clicking the same path
    } else {
      setSelectedPath(pathId);
    }
  };

  const handleFormClose = () => {
    setSelectedPath(null);
  };

  const selectedPathData = paths.find(p => p.id === selectedPath);

  return (
    <section
      id="portal"
      className="relative min-h-screen py-20 px-6 transition-all duration-700"
      style={{
        background: backgroundGradient || 'transparent'
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            How Can We Help?
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Select the option that best describes what you're looking for, and we'll guide you through the next steps
          </p>
        </motion.div>

        {/* Path cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {paths.map((path, index) => (
            <PathCard
              key={path.id}
              path={path}
              isSelected={selectedPath === path.id}
              onClick={handlePathSelect}
              index={index}
            />
          ))}
        </div>

        {/* Conversational form */}
        <AnimatePresence>
          {selectedPathData && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ConversationalForm
                path={selectedPathData}
                onClose={handleFormClose}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Helper text when no path selected */}
        {!selectedPath && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-12"
          >
            <p className="text-white/40 text-sm">
              Not sure which option is right for you?{' '}
              <button
                onClick={() => handlePathSelect('other')}
                className="text-white/60 underline hover:text-white transition-colors"
              >
                Choose "Something else"
              </button>
            </p>
          </motion.div>
        )}
      </div>

      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Scattered dots pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />

        {/* Gradient orbs based on selected path */}
        {selectedPathData && (
          <>
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.1, scale: 1 }}
              className="absolute top-20 left-10 w-96 h-96 rounded-full"
              style={{
                background: `radial-gradient(circle, ${selectedPathData.colorHex}40, transparent)`,
                filter: 'blur(100px)'
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="absolute bottom-20 right-10 w-96 h-96 rounded-full"
              style={{
                background: `radial-gradient(circle, ${selectedPathData.colorHex}30, transparent)`,
                filter: 'blur(120px)'
              }}
            />
          </>
        )}
      </div>
    </section>
  );
};

export default TriagePortal;